import { Card, CircularProgress, FormControl, Grid, Icon, Menu, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import MDAlert from "components/MDAlert";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "components/Tables/DataTable/DataTable";
import { JobPhase, JobPhaseStatus, UserPermissions } from "generated/graphql";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import { getRoute } from "utils/routing";
import { Link } from "react-router-dom";
import { useParams } from "react-router";
import { capitalCase } from "change-case";
import { useCheckPermissions } from "hooks/authentication/useCheckPermissions";
import dayjs from "dayjs";
import React, { useCallback, useState } from "react";
import useUpdateJobPhaseStatus from "hooks/jobs/job-phases/useUpdateJobPhaseStatus";

const ORDERED_PHASE_STATUSES = [
  JobPhaseStatus.TO_BE_SCHEDULED,
  JobPhaseStatus.SCHEDULED,
  JobPhaseStatus.IN_PROGRESS,
  JobPhaseStatus.WORK_COMPLETE,
  JobPhaseStatus.JOB_COSTING,
  JobPhaseStatus.READY_TO_BILL,
  JobPhaseStatus.COMPLETE,
];

function JobPhaseStatusCell({ phase }: { phase: JobPhase }) {
  const [selectedStatus, setSelectedStatus] = useState<JobPhaseStatus>(phase.status as JobPhaseStatus);
  const [updateJobPhaseStatus, { loading, error }] = useUpdateJobPhaseStatus({ id: phase.id });

  const handleStatusChange = async (event: SelectChangeEvent<JobPhaseStatus>) => {
    const newStatus = event.target.value as JobPhaseStatus;
    setSelectedStatus(newStatus);

    try {
      await updateJobPhaseStatus({
        variables: {
          id: phase.id,
          status: newStatus,
        },
      });
    } catch (mutationError) {
      setSelectedStatus(phase.status as JobPhaseStatus);
    }
  };

  return (
    <MDBox>
      <FormControl fullWidth size="small">
        <Select
          value={selectedStatus}
          onChange={handleStatusChange}
          disabled={loading}
          sx={{
            minWidth: 180,
            height: "36px",
            borderRadius: 0,
            "& .MuiSelect-select": {
              paddingTop: "8px",
              paddingBottom: "8px",
              paddingLeft: "14px",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderRadius: 0,
            },
          }}
        >
          {ORDERED_PHASE_STATUSES.map((status) => (
            <MenuItem key={status} value={status}>
              {capitalCase(status)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {loading && (
        <MDBox mt={1} display="flex" justifyContent="center">
          <CircularProgress size={16} color="inherit" />
        </MDBox>
      )}
      {error && (
        <MDBox mt={1}>
          <MDAlert color="error">Unable to update phase status.</MDAlert>
        </MDBox>
      )}
    </MDBox>
  );
}

function ProductSummaryActionsCell({
  jobId,
  jobPhaseId,
  canInputJobCosts,
}: {
  jobId: string;
  jobPhaseId: string;
  canInputJobCosts: boolean;
}) {
  const [anchorElement, setAnchorElement] = useState<Element | null>(null);

  const closeMenu = useCallback(() => {
    setAnchorElement(null);
  }, []);

  return (
    <>
      <Menu
        anchorEl={anchorElement}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        open={Boolean(anchorElement)}
        onClose={closeMenu}
        keepMounted
      >
        <MenuItem
          component={Link}
          target="_blank"
          rel="noreferrer"
          onClick={closeMenu}
          to={getRoute("jobs.view.workorder", [
            ["jobId", jobId],
            ["jobPhaseId", jobPhaseId],
          ])}
        >
          View Work Order
        </MenuItem>
        {canInputJobCosts && (
          <MenuItem
            component={Link}
            onClick={closeMenu}
            to={getRoute("jobs.view.phase", [
              ["jobId", jobId],
              ["jobPhaseId", jobPhaseId],
            ])}
          >
            Input Job Costs
          </MenuItem>
        )}
      </Menu>
      <MDTypography
        color="secondary"
        display="flex"
        alignItems="center"
        justifyContent="center"
        width="100%"
        onClick={(event: React.MouseEvent<HTMLDivElement, MouseEvent>) =>
          setAnchorElement(event.currentTarget)
        }
        sx={{ cursor: "pointer" }}
      >
        <Icon sx={{ fontWeight: "bold" }}>more_vert</Icon>
      </MDTypography>
    </>
  );
}

export default function ProductSummarySection({
  phases,
  onClose,
}: {
  phases: JobPhase[];
  onClose?: () => void;
}) {
  const { hasPermissions } = useCheckPermissions();
  const { jobId } = useParams();

  const columns = [
    {
      Header: "Phase",
      accessor: "proposalProduct.material.name",
      width: "20%",
    },
    {
      Header: "Status",
      accessor: "status",
      width: "14%",
      Cell: (props) => <JobPhaseStatusCell phase={props.row.original} />,
    },
    {
      Header: "Measurement Size",
      accessor: "jobSize",
      width: "13%",
      align: "right",
    },
    {
      Header: "Total",
      accessor: "proposedOverallTotal",
      width: "12%",
      align: "right",
      Cell: (props) => <>{formatCentsToUSD(props.value)}</>,
    },
    {
      Header: "Start Date",
      accessor: "startsAt",
      width: "10%",
      Cell: (props) =>
        props.row.original.status === JobPhaseStatus.TO_BE_SCHEDULED ? (
          <Link to={`/jobs/schedule?jobId=${jobId ?? props.row.original.job.id}`} onClick={onClose}>
            Add to schedule
          </Link>
        ) : (
          <>{props.value ? dayjs.utc(props.value).local().format("MM/DD/YYYY") : ""}</>
        ),
    },
    {
      Header: "Job Hours",
      accessor: "jobDuration",
      width: "8%",
      align: "right",
      Cell: (props) => <>{Number(props.value || 0).toFixed(1)}</>,
    },
    {
      Header: "Actions",
      accessor: "id",
      width: "6%",
      align: "center",
      Cell: (props) => (
        <ProductSummaryActionsCell
          jobId={jobId}
          jobPhaseId={props.value}
          canInputJobCosts={
            [
              JobPhaseStatus.TO_BE_SCHEDULED,
              JobPhaseStatus.SCHEDULED,
              JobPhaseStatus.IN_PROGRESS,
              JobPhaseStatus.WORK_COMPLETE,
              JobPhaseStatus.JOB_COSTING,
              JobPhaseStatus.READY_TO_BILL,
              JobPhaseStatus.COMPLETE,
            ].includes(props.row.original.status) &&
            hasPermissions(UserPermissions.INPUT_JOB_INFO_COSTS)
          }
        />
      ),
    },
  ];
  const tableData = { columns, rows: phases };

  return (
    <Card>
      <MDBox mt={2} p={2}>
        <MDTypography>Job Summary</MDTypography>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12}>
            <DataTable table={tableData} showTotalEntries={false} compact contentPaddingX={1.5} />
          </Grid>
        </Grid>
      </MDBox>
    </Card>
  );
}
